import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const AssetManager = () => (
  <Page
    title="Asset Manager"
    intro="The Asset Manager holds a record for every item of equipment — medical devices, bags, moving and handling kit, medical gas, IT equipment — with its own history of defects, maintenance, comments, and attachments."
  >
    <Section title="Browsing assets">
      <Bullets>
        <Bullet>
          Open <DocLink url="/asset-manager" text="Asset Manager" /> and
          search by asset ID, category, or Make Ready list — or click{' '}
          <strong>QR Scan</strong> and scan an asset's label to jump straight
          to its record.
        </Bullet>
        <Bullet>
          Every asset record has a QR code popup so a label can be printed
          and stuck to the physical item; scanning it later finds the record
          from the Asset Manager, the defect form, or a Make Ready check.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Registering a new asset">
      <Step number={1} title="Add the asset">
        Go to <DocLink url="/asset-manager/add-new" text="Add New Asset" />{' '}
        and give it an asset tag — type one or scan the QR label you are
        about to attach.
      </Step>
      <Step number={2} title="Categorise it">
        Pick a category — Medical Device, Medical Bag, Moving and Handling,
        Medical Gas, IT Equipment, or Other — and a sub-category (for
        example, Defibrillator / Monitor or Carry Chair). Categories your
        organisation types in beyond the defaults are saved automatically
        for reuse.
      </Step>
      <Step number={3} title="Record the details">
        Add manufacturer, brand, model, and serial number, tick{' '}
        <strong>Requires Servicing</strong> if it needs a maintenance log,
        and — for bags and devices that get checked — choose the Make Ready
        load list it should be checked against.
      </Step>
      <Info title="Who can do this?">
        Creating and editing assets requires the <strong>Fleet</strong> or{' '}
        <strong>Make Ready</strong> team. Deleting an asset requires the{' '}
        <strong>Governance</strong> team. Site administrators can always do
        both.
      </Info>
    </Section>

    <Section title="The asset record">
      <Bullets>
        <Bullet>
          The record opens with a <strong>hero</strong>: the asset ID and
          category, chips for where it is and who holds it, and stat tiles
          for <strong>Location</strong>, <strong>Checked Out</strong>,{' '}
          <strong>Service Due</strong> (for assets that require servicing),{' '}
          <strong>Active Defects</strong>, and <strong>Make Ready</strong>.
          Warning chips call out an overdue (or never recorded) service and
          a missing Make Ready list — <strong>click to fix</strong>.
        </Bullet>
        <Bullet>
          Quick-action buttons start the common jobs —{' '}
          <strong>Defect</strong>, <strong>Make Ready</strong> (or{' '}
          <strong>Assign load list</strong> when none is set),{' '}
          <strong>Service</strong>, and <strong>QR Code</strong> — and the{' '}
          <strong>More</strong> menu holds <strong>Edit asset</strong>,
          check out / check in, vehicle assignment,{' '}
          <strong>Schedule downtime</strong>, and{' '}
          <strong>Delete asset</strong>.
        </Bullet>
        <Bullet>
          Everything else lives in tabs below the hero:{' '}
          <strong>Overview</strong>, <strong>Defects</strong>,{' '}
          <strong>Servicing</strong> (only for assets that require
          servicing), <strong>Planned Downtime</strong>,{' '}
          <strong>Make Ready</strong>, <strong>Attachments</strong>, and{' '}
          <strong>Notes</strong>.
        </Bullet>
        <Bullet>
          An asset marked archived shows an <strong>Archived</strong> chip on
          its hero and is left out of asset pickers and Make Ready.
        </Bullet>
      </Bullets>
      <Screenshot url="fleet-equipment/asset-record.png">
        An asset record's hero with its stat tiles and quick actions above the
        tabs.
      </Screenshot>
    </Section>

    <Section title="Where an asset lives">
      <Bullets>
        <Bullet>
          Use <strong>Assign to vehicle</strong> on the More menu to put the
          asset on a vehicle — the vehicle record's Assets tab reflects it
          immediately — and <strong>Unassign</strong> to take it back off.
        </Bullet>
        <Bullet>
          An asset can also sit <strong>inside another asset</strong> — for
          example, a syringe driver recorded inside a bag during that bag's
          Make Ready check. Its hero then shows <strong>In</strong> the
          parent asset and, when the parent is on a vehicle,{' '}
          <strong>On</strong> that call sign too — the location follows the
          parent around. <strong>Remove from</strong> the parent (on the
          More menu) frees it again.
        </Bullet>
        <Bullet>
          <strong>Check out to me</strong> signs the asset out to you — the
          hero shows who holds it, and the check-out is logged in the
          asset's comments. <strong>Check in</strong> brings it back, and is
          for the holder themselves or a manager (for when someone forgets).
          Both actions edit the asset, so they need the same Fleet or Make
          Ready access as any other asset edit.
        </Bullet>
        <Bullet>
          <strong>Schedule downtime</strong> blocks the asset out for a
          period with a start, end, and reason — a bag in quarantine, a
          device away for repair — and the windows are managed from the{' '}
          <strong>Planned Downtime</strong> tab. Scheduling, editing, and
          deleting asset downtime requires the <strong>Fleet</strong> or{' '}
          <strong>Make Ready</strong> team.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Make Ready on the asset">
      <p>
        Assets with a Make Ready load list show their check history and can
        be checked straight from the record — see{' '}
        <DocLink
          url="/fleet-equipment/make-ready-and-maintenance"
          text="Make Ready and Maintenance"
          docsPage
        />
        .
      </p>
    </Section>

    <Section title="Maintenance logs">
      <p>
        Assets marked as requiring servicing carry a maintenance log:
        maintenance date, type (for example, Routine Service), description,
        service provider, and expiry of the work. New entries are recorded
        directly on the asset record, and the Service Due tile and Servicing
        tab flag an overdue service.
      </p>
      <Info title="Who can do this?">
        Viewing maintenance logs requires the <strong>Fleet</strong> or{' '}
        <strong>Make Ready</strong> team; recording a new maintenance entry
        requires the <strong>Governance</strong> team (site administrators
        can always do both).
      </Info>
    </Section>
  </Page>
)

export default AssetManager
